import { Tooltip as TooltipPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

const TooltipProvider = ({ children }: { children: React.ReactNode }) => (
  <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={200}>
    {children}
  </TooltipPrimitive.Provider>
);

/**
 * Primer's Tooltip: a dark label on hover or keyboard focus. The child must take a ref
 * and be focusable to reach keyboard users; icon-only controls still need `aria-label`.
 * Keep `title=` for text that is only truncated.
 */
function Tooltip({
  content,
  side = "top",
  open,
  onOpenChange,
  className,
  children,
}: {
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children: React.ReactElement;
}) {
  if (!content) return children;
  return (
    <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          data-slot="tooltip-content"
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            "z-50 max-w-80 rounded-md bg-neutral-emphasis px-2 py-1 text-xs leading-4 text-on-emphasis shadow-overlay",
            "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=instant-open]:animate-in data-[state=instant-open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            className,
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-neutral-emphasis" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export { Tooltip, TooltipProvider };
